// ============================================================================
// Demo-expertise parsing & validation
//
// The AI-assign flow asks ai-agent-wf to summarize a pending request's demo
// requirements as strict JSON (description, products, timeline, audience,
// mode, business ask, and two L1–L4 expertise levels). The response arrives
// as a streamed text blob, so this module extracts candidate JSON objects from
// the text, parses each, and validates against the contract. It is a pure
// function — no fetch, no React — so the SSE-format uncertainty is isolated
// to one testable module.
// ============================================================================

export type ExpertiseLevel = 'L1' | 'L2' | 'L3' | 'L4';

export interface DemoExpertise {
  description: string;
  products: string[];
  timeline?: { startDate?: string; dueDate?: string };
  audience?: string;
  mode?: string;
  businessAsk?: string;
  expertiseSolution: ExpertiseLevel;
  expertiseDemo: ExpertiseLevel;
}

const EXPERTISE_LEVELS: readonly ExpertiseLevel[] = ['L1', 'L2', 'L3', 'L4'];

/**
 * The demo-requirements prompt sent to ai-agent-wf. Asks it to look up the
 * request in Workfront by ID and summarize the engagement's demo expectations
 * plus the minimum expertise levels required to deliver it.
 */
export const DEMO_EXPERTISE_PROMPT = `# Provide a short summary strictly in JSON format including following for the expectations and requirements listed in the issue.  Redact customer/audience names to secure PII data.

Description | field name | format1. Products/Solutions in focus| solutions | [“Product 1“,”Product 2”]
2. Timeline/Schedule/Date of the engagement : timeline | { "start_date": "2026-08-12", "due_date": "2026-08-17" }
3. Target audience summary in free text | audience | “The ABC Group, specifically their VP of Marketing and the primary end user who is new to the product.”
4. Mode of demo delivery | mode | Basic demo
5. Main business ask | business_ask | “Demonstrate product’s capabilities to address key pain points, etc.”
6. Combined expertise level L1 through L4 for the product knowledge and the demo deliver | expertise_solution | L2
7. Demo expertise level L1 through L4 | expertise_demo | L3

## Context
The request involves organizing a demo / workshop / discovery / customer meeting etc, specifically focused on one or more Adobe products (solutions). Refer to the comments and updates on the issue as well.
This engagement is supposed to be delivered by a Solutions consultant with varying expertise. In order to assign and map the right consultant, identify the complexity of the engagement requirement and map it to the minimum required level of product expertise and demo expertise that a Solution Consultant should have to deliver this engagement based on the levels given below.

### Levels
##### L1 – Foundational
Enablement Requirements:
Intro-level enablement completed with understanding of the basics and value of the solution.
Tooling Expectations:
Learners can do a standard demo in a clickable and shared environment.
Field Support Implications:
Learners are apprenticing in the field and can support 1:Many demos.
Outcome:
Learners should be able to deliver a value pitch and 101 level demo.
##### L2 – Intermediate 
Enablement Requirements:
Deeper dives into features, customer use Requests, and common integrations.
Tooling Expectations:
Learners can do an ad hoc demo in a live environment.
Field Support Implications:
Learners are supporting early-stage or low complexity single solution customer meetings.
Outcome:
Learners have completed numerous customer demos that show practical application. They should be doing ad hoc demos where they’re able to go off script.
##### L3 – Advanced
Enablement Requirements:
Covers integrations with other solutions, data migration architectures, and expert-level guidance.
Tooling Expectations:
Learners can demo product integrations and add-ons in a live environment.
Field Support Implications:
Learners are supporting all single solution customer meetings.
Outcome:
Learners can easily lead ad hoc demos and go off script live with a customer. Learners have a comprehensive understanding of the tool and multiple closed deals to show practical application.
##### L4 – Champion
Field Support Implications:
Learners can support architecture and multi-solution conversations. The most complex meetings.
Outcome:
Learners are the SME on the team for an individual product and a thought leader as the product evolves`;

/** The strict-JSON schema footer appended to the prompt so the model returns the exact shape we validate. */
export const DEMO_EXPERTISE_SCHEMA = `Respond with a single strict JSON object matching exactly this schema - all required fields must be present:
{
  "description": string,
  "solutions": string[],
  "timeline": { "start_date": string, "due_date": string } | null,
  "audience": string,
  "mode": string,
  "business_ask": string,
  "expertise_solution": "L1"|"L2"|"L3"|"L4",
  "expertise_demo": "L1"|"L2"|"L3"|"L4"
}`;

/** Build the lean message body for the demo-expertise call. */
export function buildDemoExpertiseMessage(requestId: string): string {
  return `Request ID: ${requestId}\n\n${DEMO_EXPERTISE_PROMPT}\n\n${DEMO_EXPERTISE_SCHEMA}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isValidLevel(value: unknown): value is ExpertiseLevel {
  return typeof value === 'string' && (EXPERTISE_LEVELS as readonly string[]).includes(value);
}

/** Extract every balanced top-level JSON object from the text, ignoring malformed regions. */
function extractJsonObjects(text: string): unknown[] {
  const objects: unknown[] = [];
  let i = 0;
  const n = text.length;
  while (i < n) {
    if (text[i] !== '{') {
      i += 1;
      continue;
    }
    let depth = 0;
    let inString = false;
    let escaped = false;
    let j = i;
    for (; j < n; j += 1) {
      const ch = text[j];
      if (inString) {
        if (escaped) escaped = false;
        else if (ch === '\\') escaped = true;
        else if (ch === '"') inString = false;
        continue;
      }
      if (ch === '"') {
        inString = true;
      } else if (ch === '{') {
        depth += 1;
      } else if (ch === '}') {
        depth -= 1;
        if (depth === 0) break;
      }
    }
    if (depth !== 0) break;
    try {
      objects.push(JSON.parse(text.slice(i, j + 1)));
    } catch {
      /* ignore malformed candidate */
    }
    i = j + 1;
  }
  return objects;
}

/** Map a raw contract object to the camelCased DemoExpertise, or null when invalid. */
function mapExpertise(raw: Record<string, unknown>): DemoExpertise | null {
  const { description, solutions, expertise_solution, expertise_demo } = raw;
  if (typeof description !== 'string' || description.trim() === '') return null;
  if (!Array.isArray(solutions)) return null;
  const products = solutions.map(s => String(s).trim()).filter(Boolean);
  if (products.length === 0) return null;
  if (!isValidLevel(expertise_solution) || !isValidLevel(expertise_demo)) return null;

  const result: DemoExpertise = {
    description,
    products,
    expertiseSolution: expertise_solution,
    expertiseDemo: expertise_demo,
  };

  if (isRecord(raw.timeline)) {
    const timeline: { startDate?: string; dueDate?: string } = {};
    if (typeof raw.timeline.start_date === 'string') timeline.startDate = raw.timeline.start_date;
    if (typeof raw.timeline.due_date === 'string') timeline.dueDate = raw.timeline.due_date;
    if (timeline.startDate || timeline.dueDate) result.timeline = timeline;
  }
  if (typeof raw.audience === 'string') result.audience = raw.audience;
  if (typeof raw.mode === 'string') result.mode = raw.mode;
  if (typeof raw.business_ask === 'string') result.businessAsk = raw.business_ask;

  return result;
}

/**
 * Strip a markdown code fence around JSON, if present. Tolerates an optional
 * language tag (` ```json `, ` ```javascript `, or a bare ``` fence).
 */
function stripCodeFence(message: string): string {
  const trimmed = message.trim();
  const fenceStart = trimmed.startsWith('```');
  if (!fenceStart) return trimmed;
  const newline = trimmed.indexOf('\n');
  if (newline === -1) return trimmed;
  const body = trimmed.slice(newline + 1);
  if (body.endsWith('```')) return body.slice(0, body.length - 3).trim();
  return body;
}

/**
 * Parse demo-expertise JSON out of a streamed response blob. The response can
 * arrive either as the expertise object directly (legacy shape) or wrapped in
 * one or more message envelopes (`{"message": "<fenced json>", ...}`). Returns
 * the first candidate object that validates against the contract, or null.
 */
export function parseDemoExpertise(text: string): DemoExpertise | null {
  const wrappedMessages: string[] = [];
  for (const obj of extractJsonObjects(text)) {
    if (isRecord(obj) && typeof obj.message === 'string') {
      wrappedMessages.push(stripCodeFence(obj.message));
    }
  }

  const candidates = wrappedMessages.length > 0
    ? [wrappedMessages.join(''), text]
    : [text];

  for (const candidate of candidates) {
    for (const obj of extractJsonObjects(candidate)) {
      if (isRecord(obj)) {
        const parsed = mapExpertise(obj);
        if (parsed) return parsed;
      }
    }
  }
  return null;
}
